import React, { useRef, useState, useEffect, useCallback } from "react";
import { db } from "../../firebase"; 
import { collection, addDoc, serverTimestamp, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import Swal from "sweetalert2";
import "../../styles1/VehiclescanQR.css";
import { useLocation, useNavigate } from "react-router-dom";
import { AiFillHome } from "react-icons/ai";
import { getAuth } from "firebase/auth";
import imageCompression from "browser-image-compression";
import jsQR from "jsqr";

function VehiclescanQR() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const vehicleInfoRef = useRef(null);
  const scanAnimationRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const { state } = useLocation();
  const navigate = useNavigate();

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [purpose, setPurpose] = useState("");
  const [loading, setLoading] = useState(false);
  const [hotelName, setHotelName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meter, setMeter] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [vehicleDirection, setVehicleDirection] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [qrDetected, setQrDetected] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      Swal.fire({
        icon: "success",
        title: "Back Online",
        text: "Internet connection restored",
        timer: 2000,
        showConfirmButton: false,
      });
      processPendingSubmissions();
    };

    const handleOffline = () => {
      setIsOnline(false);
      Swal.fire({
        icon: "warning",
        title: "Offline Mode",
        text: "No internet connection. Data will be saved locally.",
        timer: 3000,
        showConfirmButton: false,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check for pending submissions on mount
    loadPendingSubmissions();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Load pending submissions from localStorage
  const loadPendingSubmissions = () => {
    try {
      const stored = localStorage.getItem("pendingVehicleScans");
      if (stored) {
        const pending = JSON.parse(stored);
        setPendingSubmissions(pending);
        if (pending.length > 0 && navigator.onLine) {
          processPendingSubmissions();
        }
      }
    } catch (error) {
      console.error("Error loading pending submissions:", error);
    }
  };

  // Process pending submissions when online
  const processPendingSubmissions = async () => {
    try {
      const stored = localStorage.getItem("pendingVehicleScans");
      if (!stored) return;

      const pending = JSON.parse(stored);
      if (pending.length === 0) return;

      Swal.fire({
        title: "Syncing Data",
        text: `Uploading ${pending.length} pending submission(s)...`,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      for (const submission of pending) {
        try {
          await addDoc(collection(db, "vehicleScans"), {
            ...submission,
            createdAt: serverTimestamp(),
            syncedAt: serverTimestamp(),
          });
        } catch (error) {
          console.error("Error syncing submission:", error);
        }
      }

      localStorage.removeItem("pendingVehicleScans");
      setPendingSubmissions([]);

      Swal.fire({
        icon: "success",
        title: "Sync Complete",
        text: "All pending submissions uploaded successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error processing pending submissions:", error);
    }
  };

  // Start camera automatically with optimized settings
  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { exact: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.warn("Rear camera not available, using default camera:", err);
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 }
            },
            audio: false,
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        } catch (finalErr) {
          console.error("Camera error:", finalErr);
          Swal.fire("Camera Error", "Unable to access camera", "error");
        }
      }
    };
    startCamera();

    return () => {
      if (videoRef.current?.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (state?.hotelName) setHotelName(state.hotelName);
  }, [state]);

  // Fetch last meter reading for the vehicle (with offline handling)
  const fetchLastMeterReading = useCallback(async (vehicleNum) => {
    console.log("Fetching meter reading for:", vehicleNum);
    
    if (!navigator.onLine) {
      console.log("Offline mode - checking cache");
      // Try to get from localStorage cache
      try {
        const cached = localStorage.getItem(`lastMeter_${vehicleNum}`);
        console.log("Cached meter value:", cached);
        if (cached) {
          const lastMeter = cached;
          if (lastMeter && lastMeter.length >= 3) {
            const prefixMeter = lastMeter.slice(0, -3);
            console.log("Setting meter to:", prefixMeter);
            setMeter(prefixMeter);
            Swal.fire({
              icon: "info",
              title: "Cached Meter Reading",
              text: `Using cached reading: ${lastMeter} (Offline)`,
              timer: 2000,
              showConfirmButton: false,
            });
          }
        } else {
          console.log("No cached meter found");
        }
      } catch (error) {
        console.error("Error loading cached meter:", error);
      }
      return;
    }

    console.log("Online mode - querying Firestore");
    try {
      // Try with orderBy first (requires index)
      try {
        const q = query(
          collection(db, "vehicleScans"),
          where("vehicleNumber", "==", vehicleNum),
          orderBy("createdAt", "desc"),
          limit(1)
        );
        
        console.log("Executing Firestore query with orderBy...");
        const querySnapshot = await getDocs(q);
        console.log("Query returned", querySnapshot.size, "documents");
        
        if (!querySnapshot.empty) {
          const lastRecord = querySnapshot.docs[0].data();
          console.log("Last record data:", lastRecord);
          const lastMeter = lastRecord.meter;
          console.log("Last meter value:", lastMeter);
          
          // Cache the meter reading
          localStorage.setItem(`lastMeter_${vehicleNum}`, lastMeter);
          console.log("Cached meter value for future use");
          
          if (lastMeter && lastMeter.length >= 3) {
            const prefixMeter = lastMeter.slice(0, -3);
            console.log("Setting meter to:", prefixMeter);
            setMeter(prefixMeter);
            
            Swal.fire({
              icon: "info",
              title: "Meter Reading Auto-filled",
              text: `Previous reading: ${lastMeter}\nAuto-filled: ${prefixMeter}`,
              timer: 3000,
              showConfirmButton: false,
            });
          } else {
            console.log("Meter value too short or empty");
          }
        } else {
          console.log("No previous records found for this vehicle");
          Swal.fire({
            icon: "info",
            title: "No Previous Record",
            text: "This is the first entry for this vehicle",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (indexError) {
        if (indexError.code === 'failed-precondition') {
          console.log("Index not ready, fetching all records and sorting manually...");
          
          // Fallback: Get all records for this vehicle and sort manually
          const qSimple = query(
            collection(db, "vehicleScans"),
            where("vehicleNumber", "==", vehicleNum)
          );
          
          const querySnapshot = await getDocs(qSimple);
          console.log("Fallback query returned", querySnapshot.size, "documents");
          
          if (!querySnapshot.empty) {
            // Sort manually by createdAt
            const docs = querySnapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .sort((a, b) => {
                const aTime = a.createdAt?.toMillis() || 0;
                const bTime = b.createdAt?.toMillis() || 0;
                return bTime - aTime; // Descending
              });
            
            const lastRecord = docs[0];
            console.log("Last record (sorted manually):", lastRecord);
            const lastMeter = lastRecord.meter;
            console.log("Last meter value:", lastMeter);
            
            // Cache the meter reading
            localStorage.setItem(`lastMeter_${vehicleNum}`, lastMeter);
            
            if (lastMeter && lastMeter.length >= 3) {
              const prefixMeter = lastMeter.slice(0, -3);
              console.log("Setting meter to:", prefixMeter);
              setMeter(prefixMeter);
              
              Swal.fire({
                icon: "info",
                title: "Meter Reading Auto-filled",
                text: `Previous reading: ${lastMeter}\nAuto-filled: ${prefixMeter}\n\n⚠️ Create Firestore index for better performance`,
                timer: 4000,
                showConfirmButton: false,
              });
            }
          } else {
            console.log("No previous records found for this vehicle");
            Swal.fire({
              icon: "info",
              title: "No Previous Record",
              text: "This is the first entry for this vehicle",
              timer: 2000,
              showConfirmButton: false,
            });
          }
        } else {
          throw indexError; // Re-throw if it's not an index error
        }
      }
    } catch (error) {
      console.error("Error fetching last meter reading:", error);
      console.error("Error details:", error.message, error.code);
      
      // Try cached version as fallback
      try {
        const cached = localStorage.getItem(`lastMeter_${vehicleNum}`);
        console.log("Fallback to cached value:", cached);
        if (cached && cached.length >= 3) {
          const prefixMeter = cached.slice(0, -3);
          console.log("Setting meter from cache:", prefixMeter);
          setMeter(prefixMeter);
          
          Swal.fire({
            icon: "warning",
            title: "Using Cached Data",
            text: `Previous reading: ${cached}\n(Could not connect to server)`,
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch (cacheError) {
        console.error("Cache fallback failed:", cacheError);
      }
    }
  }, []);

  // QR Code scanning function with useCallback (works offline) - OPTIMIZED
  const scanQRCode = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || qrDetected) return;

    // Throttle scanning to reduce CPU usage
    const now = Date.now();
    if (now - lastScanTimeRef.current < 100) { // Scan every 100ms instead of every frame
      if (isScanning) {
        scanAnimationRef.current = requestAnimationFrame(scanQRCode);
      }
      return;
    }
    lastScanTimeRef.current = now;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    // Check if video is ready
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      if (isScanning) {
        scanAnimationRef.current = requestAnimationFrame(scanQRCode);
      }
      return;
    }

    const context = canvas.getContext("2d", { willReadFrequently: true });

    // Use smaller resolution for faster scanning
    const scale = 0.5; // Reduce size by 50% for faster processing
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    
    // Scan with optimized settings
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: "dontInvert", // Skip inversion for speed
    });

    if (code) {
      setQrDetected(true);
      setIsScanning(false);
      const detectedVehicle = code.data.toUpperCase();
      console.log("QR Code detected:", detectedVehicle);
      setVehicleNumber(detectedVehicle);

      // Bring vehicle info fields into view immediately after a successful scan.
      vehicleInfoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      
      // Fetch last meter reading for this vehicle
      console.log("Calling fetchLastMeterReading...");
      fetchLastMeterReading(detectedVehicle);
      
      // Success feedback
      Swal.fire({
        icon: "success",
        title: "QR Code Detected!",
        text: `Vehicle: ${detectedVehicle}${!navigator.onLine ? ' (Offline Mode)' : ''}`,
        timer: 2000,
        showConfirmButton: false,
      });
    } else if (isScanning) {
      scanAnimationRef.current = requestAnimationFrame(scanQRCode);
    }
  }, [qrDetected, isScanning, fetchLastMeterReading]);

  // Start scanning
  const startScanning = () => {
    setIsScanning(true);
    setQrDetected(false);
    setLoading(true);
  };

  // Stop scanning
  const stopScanning = () => {
    setIsScanning(false);
    setLoading(false);
    if (scanAnimationRef.current) {
      cancelAnimationFrame(scanAnimationRef.current);
    }
  };

  useEffect(() => {
    if (isScanning && !qrDetected) {
      scanAnimationRef.current = requestAnimationFrame(scanQRCode);
    }
    return () => {
      if (scanAnimationRef.current) {
        cancelAnimationFrame(scanAnimationRef.current);
      }
    };
  }, [isScanning, qrDetected, scanQRCode]);

  // Re-Scan
  const handleRescan = (showAlert = false) => {
    setVehicleNumber("");
    setPurpose("");
    setMeter("");
    setUploadedImages([]);
    setVehicleDirection("");
    setQrDetected(false);
    stopScanning();

    if (showAlert) {
      Swal.fire("Reset", "All fields cleared. Ready to re-scan!", "info");
    }
  };

  // Handle camera capture
  const handleCameraCapture = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedImages((prev) => [...prev, file]);
      Swal.fire({
        icon: "info",
        title: "Photo Captured",
        text: `Photo added to upload list.`,
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    try {
      const options = { maxSizeMB: 0.5, maxWidthOrHeight: 1024, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);

      const formData = new FormData();
      formData.append("file", compressedFile);
      formData.append("upload_preset", "vehicalscan");
      formData.append("cloud_name", "dmrobupcg");

      const res = await fetch(`https://api.cloudinary.com/v1_1/dmrobupcg/image/upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      return data.secure_url;
    } catch (error) {
      console.error("Compression/Upload error:", error);
      return null;
    }
  };

  // Convert image file to base64 for offline storage
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const auth = getAuth();
  const userEmail = auth.currentUser?.email || null;

  // Submit form (with offline support)
  const handleSubmit = async () => {
    if (!vehicleNumber) {
      Swal.fire("Missing Vehicle Number", "Please scan the QR code before submitting.", "warning");
      return;
    }
    if (!vehicleDirection) {
      Swal.fire("Missing Selection", "Please select Vehicle IN or OUT.", "warning");
      return;
    }
    if (!purpose.trim()) {
      Swal.fire("Missing Purpose", "Please enter the purpose of trip.", "warning");
      return;
    }
    if (!meter.trim()) {
      Swal.fire("Missing Meter Reading", "Please enter the count of meter.", "warning");
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        vehicleNumber,
        vehicleDirection,
        purpose,
        meter,
        hotelName,
        userEmail,
        offlineCreatedAt: new Date().toISOString(),
      };

      // If offline, save to localStorage
      if (!navigator.onLine) {
        // Convert images to base64 for offline storage
        const base64Images = [];
        for (const img of uploadedImages) {
          try {
            const base64 = await fileToBase64(img);
            base64Images.push(base64);
          } catch (error) {
            console.error("Error converting image:", error);
          }
        }

        submissionData.uploadedImages = base64Images;
        submissionData.uploadedUrls = []; // Will be uploaded when online

        // Save to localStorage
        const stored = localStorage.getItem("pendingVehicleScans");
        const pending = stored ? JSON.parse(stored) : [];
        pending.push(submissionData);
        localStorage.setItem("pendingVehicleScans", JSON.stringify(pending));
        setPendingSubmissions(pending);

        // Cache the meter reading
        localStorage.setItem(`lastMeter_${vehicleNumber}`, meter);

        Swal.fire({
          icon: "success",
          title: "Saved Offline!",
          text: "Data saved locally. Will sync when online.",
          confirmButtonText: "OK",
        });

        handleRescan();
        setIsSubmitting(false);
        return;
      }

      // If online, proceed with normal upload
      let uploadedUrls = [];

      if (uploadedImages.length > 0) {
        Swal.fire({
          title: "Uploading...",
          text: "Please wait while images are being uploaded.",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        for (const img of uploadedImages) {
          const url = await uploadToCloudinary(img);
          if (url) uploadedUrls.push(url);
        }
        Swal.close();
      }

      submissionData.uploadedUrls = uploadedUrls;

      await addDoc(collection(db, "vehicleScans"), {
        ...submissionData,
        createdAt: serverTimestamp(),
      });

      // Cache the meter reading
      localStorage.setItem(`lastMeter_${vehicleNumber}`, meter);

      Swal.fire("Submitted!", "Vehicle scan has been saved successfully.", "success");
      handleRescan();
    } catch (error) {
      console.error("Error saving:", error);
      Swal.fire("Error", "Something went wrong while saving!", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vehiclescan-qr-container">
      <button
        className="home-icon-button"
        onClick={() => navigate("/securitydash")}
        title="Back to Dashboard"
      >
        <AiFillHome className="home-icon" />
      </button>

      <div className="header-section">
        <h2>Vehicle QR Scanner</h2>
        <div className="hotel-badge">
          <span className="hotel-label">Hotel:</span>
          <span className="hotel-name">{hotelName || "Not Found"}</span>
        </div>
        <div className={`connection-status ${isOnline ? 'online' : 'offline'}`}>
          <span className="status-indicator"></span>
          <span className="status-text">{isOnline ? 'Online' : 'Offline'}</span>
          {pendingSubmissions.length > 0 && (
            <span className="pending-badge">{pendingSubmissions.length} pending</span>
          )}
        </div>
      </div>

      <div className="camera-section">
        <div className="camera-box">
          <video ref={videoRef} autoPlay playsInline className="camera-video" />
          {isScanning && (
            <div className="scan-overlay">
              <div className="scan-line"></div>
              <div className="corner top-left"></div>
              <div className="corner top-right"></div>
              <div className="corner bottom-left"></div>
              <div className="corner bottom-right"></div>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        </div>

        <div className="scan-controls">
          {!qrDetected ? (
            <button 
              className={`scan-btn ${isScanning ? 'scanning' : ''}`}
              onClick={isScanning ? stopScanning : startScanning}
              disabled={loading && !isScanning}
            >
              {isScanning ? (
                <>
                  <span className="btn-icon">⏸</span>
                  Stop Scanning
                </>
              ) : (
                <>
                  <span className="btn-icon">📷</span>
                  Scan QR Code
                </>
              )}
            </button>
          ) : (
            <button className="rescan-btn" onClick={handleRescan}>
              <span className="btn-icon">🔄</span>
              Re-Scan
            </button>
          )}
        </div>

        {loading && !qrDetected && (
          <p className="status-text scanning">Scanning for QR code...</p>
        )}
        {qrDetected && (
          <p className="status-text success">✓ QR Code Detected!</p>
        )}
      </div>

      <div className="form-section" ref={vehicleInfoRef}>
        <div className="input-group">
          <label>Vehicle Number</label>
          <input
            type="text"
            value={vehicleNumber}
            onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
            className="vehicle-input"
            placeholder="Detected Vehicle Number"
            readOnly={qrDetected}
          />
        </div>

        <div className="input-group">
          <label>Vehicle Direction</label>
          <div className="radio-group">
            <label className={`radio-label ${vehicleDirection === "IN" ? 'active' : ''}`}>
              <input
                type="radio"
                value="IN"
                checked={vehicleDirection === "IN"}
                onChange={(e) => setVehicleDirection(e.target.value)}
              />
              <span className="radio-text">IN</span>
            </label>
            <label className={`radio-label ${vehicleDirection === "OUT" ? 'active' : ''}`}>
              <input
                type="radio"
                value="OUT"
                checked={vehicleDirection === "OUT"}
                onChange={(e) => setVehicleDirection(e.target.value)}
              />
              <span className="radio-text">OUT</span>
            </label>
          </div>
        </div>

        <div className="input-group">
          <label>Purpose of Trip</label>
          <textarea
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Enter purpose of trip..."
            rows="3"
          />
        </div>

        <div className="input-group">
          <label>Vehicle Meter Reading</label>
          <input
            type="text"
            value={meter}
            onChange={(e) => setMeter(e.target.value)}
            placeholder="Enter meter reading..."
          />
        </div>

        <div className="input-group">
          <label>Vehicle Photos (Optional)</label>
          <div className="file-input-wrapper">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraCapture}
              id="photo-upload"
            />
            <label htmlFor="photo-upload" className="file-input-label">
              <span className="btn-icon">📸</span>
              Take Photo
            </label>
          </div>
        </div>

        {uploadedImages.length > 0 && (
          <div className="preview-section">
            <h4>Vehicle Photos Preview ({uploadedImages.length})</h4>
            <div className="thumbnail-grid">
              {uploadedImages.map((file, idx) => (
                <div key={idx} className="thumbnail-wrapper">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${idx + 1}`}
                    className="thumbnail"
                  />
                  <span className="thumbnail-number">{idx + 1}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <button 
          className="submit-btn" 
          onClick={handleSubmit} 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Submitting...
            </>
          ) : (
            <>
              <span className="btn-icon">✓</span>
              {isOnline ? 'Submit' : 'Save Offline'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default VehiclescanQR;
